import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { type ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { LargeArrowBackButton } from '@/common/LargeArrowBackButton';
import { AddPartnerForm } from '@/follow-up-partners/AddPartnerForm';
import { PageContainer } from '~/styles/boardStyles';
import {
  FormContainer,
  GroupHeaderWrapper,
  Title,
} from '~/styles/layoutElements';

export const Route = createFileRoute('/_app/follow-up-partners/new')({
  component: AddFollowUpPartnerScreen,
});

/**
 * How long the flash below holds this page before the redirect fires.
 *
 * Legacy's flash has no timer of its own — `bp_core_add_message` is a cookie
 * that survives the redirect and sits on the DESTINATION until the next page
 * load — so this constant is the price of rendering it on the departing route.
 * E3's `page.waitForURL(/\/follow-up-partners\/?$/)` runs on Playwright's
 * default navigation timeout, which is orders of magnitude larger.
 */
const CONFIRMATION_HOLD_MS = 1800;

/**
 * `themes/wbs/buddypress/groups/single/partners/all-partners/new.php` — the
 * *Add New Follow-up Partner* page, which is a heading, one paragraph and
 * Gravity Form 13.
 *
 * NO `creator_id` / `group_id` INPUTS. Legacy prefills both through
 * `gravity_form(..., ['creator_id' => bp_loggedin_user_id(), 'group_id' =>
 * bp_get_current_group_id()])` (`:14-17`), which renders as
 * `name='gform_field_values' value='creator_id=…&group_id=…'` — a
 * client-supplied group on a write. Under D24 the group is the caller's
 * singleton (31 coordinator groups, 31 distinct coordinator owners, 1:1), so
 * both collapse to the authenticated caller and the request schema carries
 * neither (L16).
 *
 * THE CONFIRMATION IS A MESSAGE *AND* A REDIRECT, and an earlier draft of this
 * docstring had only the second half. `custom_confirmation`
 * (`class-follow-up-partner-registration.php:96-111`) does replace the Gravity
 * Forms confirmation with a bare `['redirect' => …/partners/all-partners/]` —
 * but the line before it, `:93`, is
 * `bp_core_add_message( __( 'Follow-up partner added successfully.', 'wbs' ) )`,
 * a BuddyPress flash that renders on the page the redirect lands on. That is
 * the catalogue string §6.6 allocates as `actions.follow-up-partner-added-
 * successfully`, present in all four locales.
 *
 * DIVERGENCE, AND IT IS A PLACEMENT ONE. v3 has no app-level message channel:
 * `__root.tsx` and `_app.tsx` mount no provider, and the one precedent for
 * carrying a message across a redirect — `@/follow-up/FollowUpsAlertContext`,
 * used by the group follow-ups family — puts the state on the family SHELL and
 * renders it on the DESTINATION child. So the flash renders HERE instead, over
 * the spent form (the submit button never releases on a 201), and the redirect
 * follows it after `CONFIRMATION_HOLD_MS`: both halves of legacy's behaviour,
 * in the only order one route can express. The new row is still the second
 * confirmation — `id DESC` (DIV-3) puts it at the top of the directory the
 * redirect lands on.
 *
 * The gate lives one level up in `follow-up-partners.tsx` — this family has a
 * shell precisely because it has two children.
 */
function AddFollowUpPartnerScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [created, setCreated] = useState(false);

  const backToDirectory = (): void => {
    void navigate({ to: '/follow-up-partners/' });
  };

  useEffect(() => {
    if (!created) return;
    const timer = window.setTimeout(() => {
      void navigate({ to: '/follow-up-partners/' });
    }, CONFIRMATION_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [created, navigate]);

  return (
    <PageContainer>
      <GroupHeaderWrapper>
        {/* A bare `Title`, not `TitleWithQuestion`: the contextual-help store
            has no destination in this stack and `HelpModal` falls back silently
            to its first item on an id miss. */}
        <Title>{t('actions.add-new-follow-up-partner')}</Title>
      </GroupHeaderWrapper>

      {/* MANDATORY CONTENT, NOT CHROME — `new.php:6` renders it above the form:
          "Before you manually enter a new Partner, please check the Follow-up
          Network and make sure they are not already a Follow-up Partner."
          (WPML unit 125385, all four languages present).

          Legacy renders it as plain text with no anchor. The whole sentence is
          the link here because the catalogue entry is ONE string in four
          languages — splicing an anchor around the words *Follow-up Network*
          inside it would mean re-segmenting each translation by hand, and an
          instruction to check a directory that offers no way to reach it is
          worse than a linked sentence. */}
      <Preamble>
        <Link
          to="/follow-up-network/"
          data-testid="add-partner-network-preamble"
        >
          {t('wbs.before-you-manually-enter')}
        </Link>
      </Preamble>

      {/* `class-follow-up-partner-registration.php:93`'s flash. It is rendered
          above the form rather than in place of it because the form is already
          inert after a 201 — `AddPartnerForm` sets `submitting` and releases it
          only on the error branch — so there is nothing to guard against, and
          swapping the subtree would collapse the page height under the alert
          for the length of the hold. */}
      {created && (
        <AlertBox variant="success">
          <span data-testid="add-partner-confirmation">
            {t('actions.follow-up-partner-added-successfully')}
          </span>
        </AlertBox>
      )}

      <FormContainer>
        {/* `new.php:10`'s `post-back-btn` anchor, which targets
            `partners/all-partners`. */}
        <LargeArrowBackButton onClick={backToDirectory} />
        <AddPartnerForm
          onCreated={() => {
            setCreated(true);
          }}
        />
      </FormContainer>
    </PageContainer>
  );
}

const Preamble = styled.p`
  font-family: var(--raleway);
  font-size: 14px;
  line-height: 20px;
  margin: 0 0 24px 0;

  a {
    color: var(--wbs-red);
    text-decoration: none;

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;
